import { useEffect, useState } from "react";
import { ArrowLeft, Printer, Wallet } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { inr } from "@/lib/format";
import { LoadingState, EmptyState } from "@/components/ui/StateBoundaries";
import { NetworkIndicator } from "@/components/captain/NetworkIndicator";
import { useCaptainNetwork } from "@/hooks/useCaptainNetwork";
import {
  getOrder,
  previewBillRender,
  generateBill,
  printDuplicateBill,
  closeOrderOnServer,
  listActiveOrders,
} from "@/lib/orderApi";
import { meshTablesPath, getMeshBase } from "@/lib/captainMesh/routes";
import { serverOrderOnTable } from "@/lib/captainMesh/db";

import ReceiptText from "@/components/ReceiptText";

/**
 * Full-screen bill preview, reached two ways:
 *
 *   /captain/table/:tableId/bill  — long-press → Print Bill on a live table.
 *     The footer's PRINT BILL freezes the bill (POST /orders/{id}/bill), which
 *     also releases the table (§39.2) and parks the order in Pending Bills.
 *
 *   /captain/bill/:orderId        — a tile on the Pending Bills screen.
 *     The bill is already frozen; the footer offers PRINT DUPLICATE (a second
 *     copy, marked Duplicate on paper) and — for staff holding billing.settle —
 *     SETTLE with a payment-mode picker.
 *
 * Either way the preview text is the server's decoding of the actual printer
 * byte stream (bible §12): what this screen shows IS what the paper says,
 * alignment, grouping and currency included. Nothing is computed on the device
 * and the screen refuses to exist offline.
 */
export default function CaptainBill() {
  const { tableId, orderId } = useParams();
  const navigate = useNavigate();
  const { token, tables, user, permissions, paymentModes } = usePOS();
  const { isOnline } = useCaptainNetwork();

  const pendingMode = Boolean(orderId);
  const table = tableId
    ? tables?.find(t => t.id === parseInt(tableId) || t.id === tableId)
    : null;

  const canSettle = Array.isArray(permissions)
    ? permissions.includes("billing.settle")
    : user?.role !== "captain";

  const [state, setState] = useState({ phase: "loading" });
  const [busy, setBusy] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);

  const back = () => navigate(pendingMode ? (getMeshBase() === "/billing" ? "/billing" : "/captain/pending-bills") : meshTablesPath());

  useEffect(() => {
    let stale = false;
    (async () => {
      if (!token) return;
      if (!isOnline) {
        setState({ phase: "offline" });
        return;
      }
      try {
        let resolvedId = orderId ? parseInt(orderId) : null;
        if (!resolvedId) {
          if (!table) return;
          // The order can be known to this device (mesh mapping) or only to the
          // server (opened on the till, or before this device's IndexedDB was
          // fresh). Resolve locally first, fall back to the live board.
          // The live board by table id is the answer online (this screen
          // only loads online). The first local capture for the table could be
          // an earlier party's finished order.
          const board = await listActiveOrders(token);
          resolvedId = serverOrderOnTable(board, table.id)?.id ?? null;
        }
        if (!resolvedId) {
          if (!stale) setState({ phase: "no-order" });
          return;
        }

        const [order, rendered] = await Promise.all([
          getOrder(resolvedId, token),
          previewBillRender(resolvedId, token),
        ]);
        if (!stale) setState({ phase: "ready", order, rendered });
      } catch (e) {
        if (!stale) setState({ phase: "error", detail: e?.response?.data?.detail || "Could not load the bill" });
      }
    })();
    return () => { stale = true; };
  }, [token, table, orderId, isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

  const printFresh = async () => {
    if (state.phase !== "ready" || busy) return;
    setBusy(true);
    try {
      const fresh = await getOrder(state.order.id, token);
      if (fresh.status === "billing") {
        // Billed since this screen loaded (another terminal, or an earlier tap
        // whose answer was lost). Generating again is refused ("billing ->
        // billing", six taps in a row on 2026-10-22); the bill exists, so print
        // a copy of it.
        await printDuplicateBill(state.order.id, token);
        toast.success(`Bill #${fresh.order_number} was already printed — printing a copy`);
        navigate(meshTablesPath());
        return;
      }
      await generateBill(state.order.id, {}, fresh.version, token);
      toast.success(`Bill printed${table ? ` for ${table.name}` : ""} — table released, bill is in Pending Bills`);
      navigate(meshTablesPath());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Print failed");
    } finally {
      setBusy(false);
    }
  };

  const printDuplicate = async () => {
    if (state.phase !== "ready" || busy) return;
    setBusy(true);
    try {
      await printDuplicateBill(state.order.id, token);
      toast.success(`Duplicate bill printing for #${state.order.order_number}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Duplicate print failed");
    } finally {
      setBusy(false);
    }
  };

  const settle = async mode => {
    if (busy) return;
    setBusy(true);
    try {
      const fresh = await getOrder(state.order.id, token);
      await closeOrderOnServer(state.order.id, mode.name, fresh.version, token);
      toast.success(`Order #${state.order.order_number} settled · ${mode.display_label || mode.name}`);
      navigate(getMeshBase() === "/billing" ? "/billing" : "/captain/pending-bills");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) toast.error("You are not allowed to settle bills — ask the admin to grant it");
      else toast.error("Could not settle — try again");
    } finally {
      setBusy(false);
      setSettleOpen(false);
    }
  };

  const isBilled = state.phase === "ready" && state.order.status === "billing";
  const modes = (paymentModes || []).filter(m => m.is_active !== false);

  return (
    <div className="flex flex-col h-full bg-background" data-testid="captain-bill-screen">
      <header className="min-h-[3.5rem] pt-safe flex items-center justify-between px-3 bg-primary text-primary-foreground shadow-md shrink-0">
        <div className="flex items-center gap-2">
          <button
            className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full hover:bg-primary-foreground/20"
            aria-label={pendingMode ? "Back to pending bills" : "Back to tables"}
            onClick={back}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex flex-col">
            <small className="text-micro font-bold opacity-80 uppercase">
              {isBilled ? "Pending bill" : "Bill Preview"}
              {isBilled && state.order?.bill_number != null && ` · Bill ${state.order.bill_number}`}
            </small>
            <h2 className="text-body font-bold leading-tight">
              {table?.name || (state.phase === "ready" ? `Order #${state.order.order_number}` : "…")}
            </h2>
          </div>
        </div>
        <NetworkIndicator />
      </header>

      <div className="flex-1 overflow-y-auto p-base">
        {state.phase === "loading" && <LoadingState message="Asking the server for the bill…" />}
        {state.phase === "offline" && (
          <EmptyState
            title="Bill needs the server"
            message="Billing is computed and printed by the server. Reconnect to preview or print this bill."
          />
        )}
        {state.phase === "no-order" && (
          <EmptyState title="No running order" message="This table has no order to bill." />
        )}
        {state.phase === "error" && <EmptyState title="Could not load the bill" message={String(state.detail)} />}

        {state.phase === "ready" && (
          <div
            className="max-w-sm mx-auto bg-card border rounded-lg shadow-sm p-4"
            data-testid="bill-receipt"
          >
            {/* The server decoded the actual printer byte stream (bible §12) —
                this text IS the paper, spacing and alignment included, so it
                renders line by line in a monospace block sized to fit the card
                and is never re-formatted here. Rule lines alone become CSS
                strokes at content width; every other line is verbatim. */}
            {/* Fit-to-width in two passes, so large phone text never pushes the
                paper off screen (owner, 2026-10-25). */}
            <ReceiptText text={state.rendered.text} testId="bill-render-text" />
          </div>
        )}
      </div>

      <footer
        className="p-4 bg-card border-t shrink-0 flex gap-3"
        style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))" }}
      >
        {isBilled ? (
          <>
            <button
              className="flex-1 h-14 bg-muted text-foreground border rounded-xl font-bold uppercase text-body tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
              onClick={printDuplicate}
              disabled={busy}
              data-testid="print-duplicate-button"
            >
              <Printer className="w-5 h-5" />
              Print Duplicate
            </button>
            {canSettle && (
              <button
                className="flex-1 h-14 bg-action text-action-foreground rounded-xl font-bold uppercase text-body tracking-wider shadow-md flex items-center justify-center gap-2 disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none"
                onClick={() => setSettleOpen(true)}
                disabled={busy}
                data-testid="settle-button"
              >
                <Wallet className="w-5 h-5" />
                Settle
              </button>
            )}
          </>
        ) : (
          <button
            className="w-full h-14 bg-action text-action-foreground rounded-xl font-bold uppercase text-body tracking-wider shadow-md flex items-center justify-center gap-2 disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none"
            onClick={printFresh}
            disabled={state.phase !== "ready" || busy}
            data-testid="print-bill-button"
          >
            <Printer className="w-5 h-5" />
            {busy ? "Printing…" : "Print Bill"}
          </button>
        )}
      </footer>

      {settleOpen && state.phase === "ready" && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          data-testid="settle-dialog"
        >
          <div className="bg-card w-full max-w-sm rounded-2xl shadow-xl border overflow-hidden">
            <div className="px-4 py-3 border-b">
              <strong className="text-body font-bold text-foreground block">
                Settle #{state.order.order_number} · {inr(state.rendered.total)}
              </strong>
              <small className="text-micro font-bold uppercase text-muted-foreground">How did they pay?</small>
            </div>
            <div className="p-3 grid grid-cols-2 gap-2">
              {modes.map(mode => (
                <button
                  key={mode.id}
                  className="h-14 rounded-xl border bg-background text-body font-bold text-foreground hover:bg-muted active:scale-95 transition-transform flex items-center justify-center gap-2 disabled:opacity-50"
                  onClick={() => settle(mode)}
                  disabled={busy}
                  data-testid={`settle-mode-${mode.name}`}
                >
                  <Wallet className="w-4 h-4" />
                  {mode.display_label || mode.name}
                </button>
              ))}
              {!modes.length && (
                <p className="col-span-2 text-body text-muted-foreground p-2">No active payment modes configured.</p>
              )}
            </div>
            <button
              className="w-full h-12 text-body font-bold text-muted-foreground border-t hover:bg-muted/40"
              onClick={() => setSettleOpen(false)}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
